import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import qs from 'query-string';
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import { Alert } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { useAuth, usePost } from 'hooks';
import { failedLogin } from 'services/api';

import licenseType from 'models/licenseType';
import { ResearcherSearch } from 'components/Search/Results/helpers';
import { clearFilters, setView } from 'actions/search';
import { useDispatch, useSelector } from 'react-redux';
import { getSearchView } from 'selectors/search';
import { view } from 'components/Search/Results/constants';
import { Button } from 'components/common';
import { SESSION_ID_KEY, CLIENT_TYPES } from 'utils/signalr/messageValidators';

import FromSalesforce from './FromSalesforce';
import { Container, InnerContent, ContentBody } from './styled';
import CredentialsForm from './CredentialsForm';
import CodeForm from './CodeForm';
import SsoProviderSelector from './SsoProviderSelector';
import { tfaHasBeenEnabledDownloadFile } from './tfaConstants';
import {
  getPreConditionError,
  getErrorMessage,
  getErrorForLog,
} from './errors';
import { LicenceExpiredMessage } from './licenceExpiredMessage';

const REMEMBER_ME_USERNAME_KEY = 'REMEMBER_ME_USERNAME_KEY';

const Login = () => {
  const {
    loggingIn,
    loginError,
    logIn,
    externalLogIn,
    externalLoggingIn,
    externalLoginError,
    logOut,
    isAuthed,
    verifyCode,
    verifyCodeError,
    isVerifying,
    user,
    hasLicense,
    softLogOut,
  } = useAuth();
  const [error, setError] = useState();
  const emailRef = useRef();
  const [, postFailedLogin] = usePost({ url: failedLogin });
  const rememberMeUsername = localStorage.getItem(REMEMBER_ME_USERNAME_KEY);
  const [form, setForm] = useState({
    username: rememberMeUsername || '',
    password: '',
    rememberMe: !!rememberMeUsername,
  });
  const [code, setCode] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(true);
  const [userEmail, setUserEmail] = useState(rememberMeUsername || '');
  const { search } = useLocation();
  const { push } = useHistory();
  const {
    redirect,
    clientType: queryClientType,
    sessionId: querySessionId,
    ...queryParams
  } = qs.parse(search);
  const searchView = useSelector(getSearchView);
  const dispatch = useDispatch();

  // Determine client type from query param, default to 'browser' if not present
  // IMPORTANT: Do NOT store in localStorage - this is a one-shot for extension login
  const clientType = queryClientType || CLIENT_TYPES.BROWSER;

  // For sessionId: prefer query param (from extension), otherwise use/generate browser's sessionId
  const sessionId = useMemo(() => {
    // If sessionId provided via query param (extension flow), use it
    if (querySessionId) return querySessionId;

    // Otherwise, standard browser flow
    const existingId = localStorage.getItem(SESSION_ID_KEY);
    if (existingId) return existingId;
    const newId = uuidv4();
    localStorage.setItem(SESSION_ID_KEY, newId);
    return newId;
  }, [querySessionId]);

  emailRef.current = showPasswordForm ? form.username : userEmail;

  useEffect(() => {
    logOut();
  }, [logOut]);

  useEffect(() => {
    if (loginError) {
      let error;
      // 412 code is used for different errors
      if (loginError.status === 412) {
        error = getPreConditionError(loginError);

        // Check if SSO is required - auto-redirect to SSO flow
        if (error === 'ssoRequired') {
          setUserEmail(form.username);
          setShowPasswordForm(false); // Shows SsoProviderSelector
          setError(null);
          return;
        }
      } else {
        error = getErrorForLog(loginError);
      }

      postFailedLogin({ email: emailRef.current, error });
      setError(getErrorMessage(loginError));
    }
  }, [postFailedLogin, loginError, form.username]);

  useEffect(() => {
    if (externalLoginError) {
      setError(getErrorMessage(externalLoginError));
    }
  }, [externalLoginError]);

  useEffect(() => {
    setError(verifyCodeError?.message);
    if (verifyCodeError?.status === 412) {
      setForm({
        username: '',
        password: '',
        rememberMe: false,
      });
    }
    setCode('');
    const { fcWidget } = window;
    if (verifyCodeError && !fcWidget?.isOpen()) {
      fcWidget?.open();
    }
  }, [verifyCodeError]);

  const handleChange = key => value => {
    setForm({
      ...form,
      [key]: value,
    });
  };

  const noDetails = !form.username || !form.password;

  const handleLogin = () => {
    if (noDetails) return;
    setError(null);
    if (form.rememberMe) {
      localStorage.setItem(REMEMBER_ME_USERNAME_KEY, form.username);
    } else {
      localStorage.removeItem(REMEMBER_ME_USERNAME_KEY);
    }
    logIn({ ...form, sessionId, clientType });
  };

  const handleExternalLogin = (provider, credential) => {
    if (!credential || !provider) return;
    setError(null);
    externalLogIn({
      provider: provider,
      credential: credential,
      sessionId,
      clientType,
    });
  };

  const handleReset = () => {
    push('/reset');
  };

  const handleVerifyCode = () => {
    if (!code) return;
    verifyCode({ code, ...form, sessionId, clientType });
  };

  const toLogin = useCallback(() => {
    setError('');
    setCode('');
    setShowPasswordForm(false);
    softLogOut();
  }, [softLogOut]);

  const handleUsePasswordLogin = () => {
    setForm({ ...form, username: userEmail });
    setShowPasswordForm(true);
  };

  const handleContinueToSso = useCallback(() => {
    if (form.username) {
      setUserEmail(form.username);
    }
    setShowPasswordForm(false);
  }, [form.username, setShowPasswordForm, setUserEmail]);

  const handleSsoRedirect = authorizationUrl => {
    // Persist login context so SsoCallback can redirect back to the extension
    // (or any other redirect target) after the IdP round-trip.
    sessionStorage.setItem(
      'ssoLoginContext',
      JSON.stringify({ redirect, clientType, sessionId, queryParams })
    );
    window.location.href = authorizationUrl;
  };

  if (isAuthed && (!user.tfaEnabled || user.totpVerified)) {
    let redirectString = `${redirect || ''}?${qs.stringify(queryParams, {
      encode: false,
    })}`;

    const hasOrgLicense = hasLicense(
      [licenseType.industry, licenseType.academic],
      true
    );
    if (!hasOrgLicense) {
      if (!ResearcherSearch(searchView)) {
        dispatch(setView(view.researcher));
        dispatch(clearFilters);
      }
      if (redirectString.startsWith('/organisation/')) {
        redirectString = '';
      }
    }
    if (redirectString.startsWith('chrome-extension://')) {
      try {
        const url = new URL(redirectString);
        const allowedExtensionIds = ['lbpneicehekpgkbkbaagmenaicffpfkk'];

        if (!allowedExtensionIds.includes(url.hostname)) {
          console.error('Untrusted extension redirect');
          return;
        }

        const token = localStorage.getItem('token') || '';
        const userName = user?.userName || '';

        url.searchParams.set('token', token);
        url.searchParams.set('user', userName);
        url.searchParams.set('sessionId', sessionId); // Pass back the sessionId from query param

        window.location.href = url.toString();
        return null;
      } catch (err) {
        window.location.href = '/';
      }
    }
    return <Redirect to={redirectString} />;
  }

  return (
    <>
      <div className='form-header'>
        {!isAuthed ? 'Login' : 'Two-Factor Authentication'}
      </div>
      <Container>
        <ContentBody>
          <InnerContent>
            {!isAuthed ? (
              showPasswordForm ? (
                <CredentialsForm
                  form={form}
                  isLoading={loggingIn || externalLoggingIn}
                  handleLogin={handleLogin}
                  handleChange={handleChange}
                  handleReset={handleReset}
                  handleExternalLogin={handleExternalLogin}
                  onContinueToSso={handleContinueToSso}
                />
              ) : (
                <SsoProviderSelector
                  email={userEmail}
                  setEmail={setUserEmail}
                  onSsoRedirect={handleSsoRedirect}
                  onUsePasswordLogin={handleUsePasswordLogin}
                  isLoading={loggingIn || externalLoggingIn}
                />
              )
            ) : (
              <CodeForm
                code={code}
                setCode={setCode}
                isLoading={isVerifying}
                handleVerifyCode={handleVerifyCode}
              />
            )}
            {!isAuthed && queryParams.download && (
              <Alert
                message={
                  queryParams.status === 'tfaenabled'
                    ? tfaHasBeenEnabledDownloadFile
                    : 'Please log in to access your download'
                }
                type='info'
              />
            )}
            {!isAuthed && queryParams.status === 'userdisabled' && (
              <Alert
                message='Your user has been disabled. Please contact support to speak about your account.'
                type='info'
              />
            )}
            {!isAuthed && queryParams.status === 'accountinactive' && (
              <Alert
                message='Your account is inactive. Please contact support to speak about your account.'
                type='info'
              />
            )}
            {!isAuthed && queryParams.status === 'expiredlicence' && (
              <Alert message={<LicenceExpiredMessage />} type='info' />
            )}
            {error && <Alert message={error} type='error' />}
          </InnerContent>
          <FromSalesforce />
        </ContentBody>
      </Container>
      {isAuthed && (
        <Button
          type='link'
          disabled={isVerifying}
          onClick={toLogin}
          className='back-to-login'
        >
          Back to Login
        </Button>
      )}
    </>
  );
};

export default Login;
